import type { Bar, Timeframe } from "@alpha/shared";
import { toChartTime } from "./time";

export interface LinePoint {
  time: string | number;
  value: number;
}

/**
 * Turns a stored indicator series (aligned to `times`, null while warming up) into chart points.
 * Points before `minUnix` are dropped so an overlay never stretches the axis past the loaded bars,
 * and times are made strictly ascending and unique, which the chart requires.
 */
export function toLinePoints(
  times: readonly number[],
  values: readonly (number | null)[] | undefined,
  tf: Timeframe,
  minUnix: number,
): LinePoint[] {
  if (!values) return [];
  const out: LinePoint[] = [];
  const n = Math.min(times.length, values.length);
  for (let i = 0; i < n; i++) {
    const v = values[i];
    const t = times[i]!;
    if (v === null || v === undefined || !Number.isFinite(v) || t < minUnix) continue;
    const time = toChartTime(t, tf);
    const prev = out[out.length - 1];
    if (prev && prev.time === time) {
      prev.value = v;
    } else {
      out.push({ time, value: v });
    }
  }
  return out;
}

export function toCandles(bars: readonly Bar[], tf: Timeframe) {
  const out: { time: string | number; open: number; high: number; low: number; close: number; volume: number }[] = [];
  for (const b of bars) {
    const time = toChartTime(Math.floor(Date.parse(b.ts) / 1000), tf);
    const row = { time, open: Number(b.open), high: Number(b.high), low: Number(b.low), close: Number(b.close), volume: Number(b.volume) };
    const prev = out[out.length - 1];
    if (prev && prev.time === time) out[out.length - 1] = row;
    else out.push(row);
  }
  return out;
}

export const lastValue = (s: readonly (number | null)[] | undefined): number | null => {
  if (!s) return null;
  for (let i = s.length - 1; i >= 0; i--) {
    const v = s[i];
    if (v !== null && v !== undefined) return v;
  }
  return null;
};
